/* eslint-disable parog-web/no-glint-expect-error-directive */
import type Store from '@ember-data/store';
import { array, concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import type { BufferedChangeset } from 'ember-changeset/types';
import pluralize from 'ember-inflector/helpers/pluralize';
import t from 'ember-intl/helpers/t';
import type IntlService from 'ember-intl/services/intl';
import and from 'ember-truth-helpers/helpers/and';
import eq from 'ember-truth-helpers/helpers/eq';
import not from 'ember-truth-helpers/helpers/not';
import ForcBaseComponent from 'parog-web/components/forc/abstract/base-component';
import Provider from 'parog-web/components/forc/provider';
import capitalize from 'parog-web/helpers/capitalize';
import CompoundSurveyFrequency from 'parog-web/models/compound-survey-frequency';
import type Patient from 'parog-web/models/patient';
import VitalSignItem, {
  VITAL_SIGN_FREQUENCIES
} from 'parog-web/models/vital-sign/vital-sign-item';
import type DashboardService from 'parog-web/services/dashboard';
import Checkbox from 'ui-kit/components/checkbox';
import fuiDropdown from 'wise-fui-modules/modifiers/fui-dropdown';

interface ConstantItem {
  enabled: boolean;
  frequency?: string;
  forgettingNumber?: number;
  label?: string;
}
class ConstantMonitoringModelView {
  @tracked isCustomDirty = false;

  get constants(): ConstantItem[] {
    return [...this.filteredVitalSigns, ...this.surveyFrequencies];
  }

  get enabledConstants(): ConstantItem[] {
    return this.constants.filter(c => c.enabled);
  }

  get filteredVitalSigns(): VitalSignItem[] {
    const followUpReasonIdsToFilter = ['digest-rch', 'digest-crohn'];
    if (followUpReasonIdsToFilter.includes(this.patient.followUpReasonId)) {
      if (this.patient.followUpReasonId === 'digest-rch') {
        return this.patient.vitalSigns.filter(v => v.id !== 'crohn-survey');
      }
      if (this.patient.followUpReasonId === 'digest-crohn') {
        return this.patient.vitalSigns.filter(v => v.id !== 'rch-survey');
      }
    }
    return this.patient.vitalSigns;
  }

  constructor(
    private store: Store,
    private patient: Patient,
    private surveyFrequencies: CompoundSurveyFrequency[]
  ) {}

  async save(): Promise<void> {
    // Create a new CompoundSurveyFrequenciesChangedJe that contains only enabled frequencies
    const freqChangedJeReq = this.store
      .createRecord('compound-survey-frequencies-changed-journal-event', {
        patient: this.patient,
        occurredAt: new Date(),
        source: 'wise',
        enabledSurveyFrequencies: this.surveyFrequencies.filter(f => f.enabled)
      })
      .save();
    // @ts-expect-error TO BE VALIDATED BY PRODUCT ♻️ `constantMonitoringChangedJournalEvent` might be undefined
    this.patient.constantMonitoringChangedJournalEvent.source = 'wise';
    await Promise.all([
      // @ts-expect-error TO BE VALIDATED BY PRODUCT ♻️ `constantMonitoringChangedJournalEvent` might be undefined
      this.patient.constantMonitoringChangedJournalEvent.save(),
      freqChangedJeReq
    ]);
  }
}

interface ConstantMonitoringChangeset extends BufferedChangeset {
  enabledConstants: ConstantItem[];
}

export default class ConstantMonitoringEditionComponent extends ForcBaseComponent {
  static fullComponentId = 'constant-monitoring/edition-form';

  @service declare dashboard: DashboardService;
  @service declare intl: IntlService;
  @service declare store: Store;

  get changeset(): ConstantMonitoringChangeset {
    if (this._changeset) return this._changeset;

    const compoundSurveyFrequencies = this.patient.surveyFrequencies;

    this._changeset = Changeset(
      new ConstantMonitoringModelView(
        this.store,
        this.patient,
        compoundSurveyFrequencies
      )
    ) as ConstantMonitoringChangeset;

    return this._changeset;
  }

  override get isDirty() {
    return this.changeset.isCustomDirty as boolean;
  }

  get shouldShowForgetting(): boolean {
    return (
      this.patient.tlsActive &&
      this.changeset.enabledConstants.some(
        constant => constant.forgettingNumber != undefined
      )
    );
  }

  get shouldShowFrequency(): boolean {
    return (
      this.patient.tlsActive &&
      this.changeset.enabledConstants.some(constant =>
        Boolean(constant.frequency)
      )
    );
  }

  private declare _changeset: ConstantMonitoringChangeset;
  private isFetchNeeded = false;

  handleCheckedOnly() {
    this.isFetchNeeded = true;
  }

  labelFromConstant = (
    constant: VitalSignItem | CompoundSurveyFrequency
  ): string => {
    if (constant instanceof VitalSignItem) {
      return this.getVitalSignLabel(constant);
    } else if (constant instanceof CompoundSurveyFrequency) {
      return this.getCompoundSurveyLabel(constant);
    }
    return '';
  };

  override async save(): Promise<void> {
    await this.changeset.save();
    if (this.isFetchNeeded) {
      await this.dashboard.fetchGraphDataTask.perform();
    }
  }

  setDirtiness = event => {
    this.changeset.set('isCustomDirty', true);
    if (event.target.checked) {
      this.handleCheckedOnly();
    }
  };

  private getCompoundSurveyLabel(item: CompoundSurveyFrequency): string {
    const survey = this.store.peekRecord('compound-survey', item.surveyId);
    if (!survey)
      throw `No corresponding compound-survey found for the id '${item.surveyId}'`;
    return survey.title;
  }

  private getVitalSignLabel(item: VitalSignItem): string {
    return this.intl.t(`vital-sign.${item.id}.long-description`);
  }

  <template>
    <Provider
      {{! @glint-expect-error }}
      @forcComponent={{this}}
      @title='Plan de suivi des données cliniques'
    >
      <:content>
        <div class='mb-4'>
          <i class='exclamation triangle icon'></i>
          Si vous modifiez manuellement une information ici, le plan de suivi du
          patient ne sera plus actualisé automatiquement par la suite en
          fonction de son état de santé.
        </div>

        <div class='field [.ui.form_.inline.fields_&.wide.field]:min-h-[1.5em]'>
          <p class='placeholder-label'>Votre patient recevra un message de
            rappel par SMS et/ou email.</p>
        </div>

        <div class='three fields inline'>
          <div class='field'>
            Donnée à suivre
          </div>

          {{#if this.shouldShowFrequency}}
            <div class='field'>
              Rappeler au patient
            </div>
          {{/if}}

          {{#if this.shouldShowForgetting}}
            <div class='field'>
              M'alerter si le patient oublie
            </div>
          {{/if}}
        </div>

        {{! @glint-expect-error }}
        {{#each this.changeset.constants as |constant|}}
          <div class='three fields inline'>
            <div class='field'>
              <Checkbox
                @labelName={{capitalize (this.labelFromConstant constant)}}
                @checked={{constant.enabled}}
                @onChange={{fn (mut constant.enabled)}}
                {{on 'change' this.setDirtiness}}
                @dataTestInput={{constant.id}}
              />
            </div>

            {{#if
              (and this.shouldShowFrequency constant.enabled constant.frequency)
            }}
              <div class='field'>
                <div
                  {{fuiDropdown onChange=(fn (mut constant.frequency))}}
                  class={{if
                    this.isReadOnly
                    'ui dropdown disabled semeia-readonly'
                    'ui dropdown'
                  }}
                  data-test-dropdown={{constant.id}}
                  {{on 'change' this.setDirtiness}}
                >
                  <input type='hidden' value={{constant.frequency}} />
                  <div class='default text'>
                    Sélectionnez une fréquence
                  </div>
                  {{#unless this.isReadOnly}}
                    <i class='dropdown icon'></i>
                  {{/unless}}

                  <div class='menu'>
                    {{#each VITAL_SIGN_FREQUENCIES as |frequency|}}
                      <div class='item' data-value={{frequency}}>
                        {{capitalize (t (concat 'frequency.' frequency))}}
                      </div>
                    {{/each}}
                  </div>
                </div>
              </div>
            {{/if}}

            {{#if
              (and
                this.shouldShowForgetting
                constant.enabled
                (not (eq constant.forgettingNumber null))
              )
            }}
              <div class='field'>
                <div
                  {{fuiDropdown onChange=(fn (mut constant.forgettingNumber))}}
                  class={{if
                    this.isReadOnly
                    'ui dropdown disabled semeia-readonly'
                    'ui dropdown'
                  }}
                  data-test-forgetting-dropdown={{constant.id}}
                  {{on 'change' this.setDirtiness}}
                >

                  <input type='hidden' value={{constant.forgettingNumber}} />
                  <div class='default text'>
                    Sélectionnez une fréquence
                  </div>
                  {{#unless this.isReadOnly}}
                    <i class='dropdown icon'></i>
                  {{/unless}}

                  <div class='menu'>
                    {{#each (array 1 2 3 4 5 6 0) as |frequency|}}
                      <div class='item' data-value={{frequency}}>
                        {{#if (eq frequency 0)}}
                          Jamais
                        {{else}}
                          Après
                          {{pluralize frequency 'oubli'}}
                        {{/if}}
                      </div>
                    {{/each}}
                  </div>
                </div>
              </div>
            {{/if}}
          </div>
        {{/each}}
      </:content>
    </Provider>
  </template>
}
